import { types as sdkTypes } from './sdkLoader';
import { unitDivisor } from './currency';
import {
  DUAL_LISTING_TYPE,
  RENT_PROCESS,
  SELL_PROCESS,
  isDualListingType,
} from '../config/configDualListing';

/**
 * Finds the Console listing type that uses the given transaction process.
 * Matching on process rather than id means a rename in Console (daily-rental ->
 * fashion-rental) does not break dual support.
 *
 * @param {Array} listingTypes hosted listing type configs
 * @param {string} processName e.g. "default-booking"
 * @returns {Object|undefined} the matching listing type config
 */
const findByProcess = (listingTypes = [], processName) =>
  listingTypes.find(
    t =>
      t.transactionType?.process === processName ||
      t.transactionType?.alias?.startsWith(`${processName}/`)
  );

/**
 * The two Console listing types a dual listing is composed from.
 * @returns {Object} { rentConfig, sellConfig } — either may be undefined
 */
const getDualHalves = (listingTypes = []) => ({
  rentConfig: findByProcess(listingTypes, RENT_PROCESS),
  sellConfig: findByProcess(listingTypes, SELL_PROCESS),
});

// Lender-facing label for a synthesized dual type. Only used when Console does
// not provide the type itself — a Console-defined label always wins.
const DUAL_LISTING_TYPE_LABEL = 'Rent and sell';

/**
 * Union of two defaultListingFields maps: a capability is enabled if either half
 * enables it. A dual listing needs the rent side's availability AND the sell
 * side's stock and delivery.
 */
const unionDefaultListingFields = (a = {}, b = {}) =>
  [...new Set([...Object.keys(a), ...Object.keys(b)])].reduce(
    (acc, key) => ({ ...acc, [key]: !!(a[key] || b[key]) }),
    {}
  );

/**
 * Adds a synthesized "both" listing type for each pair in configDualListing,
 * so lenders can choose it before it exists in Console.
 *
 * The synthesized type clones the RENT type — so its process, alias, unit type
 * and availability settings are real, Console-defined values — and unions in the
 * SELL type's enabled fields so stock and delivery are available too.
 *
 * Skipped when Console already provides the type (Console wins), or when either
 * half is missing.
 *
 * NOTE: a synthesized type is not known to Console. Listings saved with it will
 * not match a Console-side listing type, so `enforceValidListingType` must stay
 * false and the listing type filter won't list it. Define the type in Console to
 * remove these caveats — this function then does nothing.
 *
 * @param {Array} listingTypes listing types from hosted config
 * @returns {Array} listingTypes plus any synthesized dual types
 */
export const withDualListingTypes = (listingTypes = []) => {
  // For now, removed the 3rd "Rent and sell" dual option by returning listingTypes directly
  return listingTypes;

  /*
  const alreadyDefined = listingTypes.some(t => t.listingType === DUAL_LISTING_TYPE);
  if (alreadyDefined) {
    return listingTypes;
  }

  const { rentConfig, sellConfig } = getDualHalves(listingTypes);
  if (!rentConfig?.transactionType || !sellConfig?.transactionType) {
    return listingTypes;
  }

  return [
    ...listingTypes,
    {
      ...rentConfig,
      listingType: DUAL_LISTING_TYPE,
      label: DUAL_LISTING_TYPE_LABEL,
      // Stock must be tracked so a sold piece cannot be sold twice; the rent
      // half has no stock type of its own.
      stockType: sellConfig.stockType || 'oneItem',
      defaultListingFields: unionDefaultListingFields(
        rentConfig.defaultListingFields,
        sellConfig.defaultListingFields
      ),
    },
  ];
  */
};

/**
 * Extra publicData for a listing that can be both rented and bought.
 *
 * A Sharetribe listing carries one `transactionProcessAlias` and one `unitType`
 * in publicData. Those stay as the RENT path so every untouched part of the
 * template keeps working; the buy path is stored alongside under its own keys
 * and supplied explicitly at checkout.
 *
 * Aliases and unit types are read from the Console listing types referenced in
 * configDualListing, so Console remains the source of truth for them.
 *
 * @param {Array} listingTypes hosted listing type configs
 * @param {string} listingType the selected listing type id
 * @returns {Object} publicData keys to merge, or {} when not a dual type
 */
export const getDualListingPublicData = (listingTypes = [], listingType) => {
  if (!isDualListingType(listingType)) {
    return {};
  }

  const { rentConfig, sellConfig } = getDualHalves(listingTypes);

  // If either half is missing from Console, store nothing rather than a
  // half-configured listing that would fail at checkout.
  if (!rentConfig?.transactionType?.alias || !sellConfig?.transactionType?.alias) {
    return {};
  }

  return {
    rentEnabled: true,
    sellEnabled: true,
    // Primary (rent) is also written to transactionProcessAlias/unitType by the
    // caller; duplicated here so the buy path never has to infer it.
    rentProcessAlias: rentConfig.transactionType.alias,
    rentUnitType: rentConfig.transactionType.unitType,
    sellProcessAlias: sellConfig.transactionType.alias,
    sellUnitType: sellConfig.transactionType.unitType,
  };
};

/**
 * The transaction info a dual listing should store as its PRIMARY process.
 * Rent wins: it needs the availability calendar, and a booking listing degrades
 * more gracefully than a purchase one if the buy path is never configured.
 *
 * @param {Array} listingTypes hosted listing type configs
 * @param {string} listingType the selected listing type id
 * @returns {Object|null} { transactionProcessAlias, unitType }, or null
 */
export const getPrimaryTransactionInfo = (listingTypes = [], listingType) => {
  if (!isDualListingType(listingType)) {
    return null;
  }
  const { rentConfig } = getDualHalves(listingTypes);
  const alias = rentConfig?.transactionType?.alias;
  const unitType = rentConfig?.transactionType?.unitType;
  return alias && unitType ? { transactionProcessAlias: alias, unitType } : null;
};

/**
 * Whether a saved listing can be bought, read back from publicData.
 * @param {Object} publicData listing publicData
 */
export const canBuy = publicData =>
  publicData?.sellEnabled === true && !!publicData?.sellProcessAlias;

/**
 * Whether a saved listing can be rented. Listings predating dual support have
 * no rentEnabled flag, so fall back to their process alias.
 * @param {Object} publicData listing publicData
 */
export const canRent = publicData => {
  if (publicData?.rentEnabled === true) {
    return true;
  }
  // No dual keys: fall back to the primary process. A purchase listing is not
  // rentable, everything else (booking, inquiry) is treated as rentable.
  const alias = publicData?.transactionProcessAlias;
  return !!alias && !alias.startsWith('default-purchase');
};

/**
 * A plain (non-dual) purchase listing can be bought even though it carries no
 * sellEnabled flag.
 */
const isPurchaseOnly = publicData =>
  !!publicData?.transactionProcessAlias?.startsWith('default-purchase');

/**
 * What a listing offers, in one call, for components that need to branch.
 *
 * @param {Object} publicData listing publicData
 * @returns {Object} { rent, buy, both } booleans
 */
export const getListingCapabilities = publicData => {
  const rent = canRent(publicData);
  const buy = canBuy(publicData) || isPurchaseOnly(publicData);
  return { rent, buy, both: rent && buy };
};

/**
 * The rental discount a lender has set for longer bookings, if any.
 *
 * Stored as whole percentages in publicData. Returns null when unset or out of
 * range, so callers can simply skip the note rather than render "Save 0%".
 *
 * @param {Object} publicData listing publicData
 * @returns {Object|null} { weekly, monthly } percentages, nulls omitted
 */
export const getRentalDiscounts = publicData => {
  const valid = v => (typeof v === 'number' && v > 0 && v < 100 ? v : null);
  const weekly = valid(publicData?.weeklyDiscountPercent);
  const monthly = valid(publicData?.monthlyDiscountPercent);
  return weekly || monthly ? { weekly, monthly } : null;
};

/**
 * Re-frames a dual listing as the single-path listing the chosen mode implies.
 *
 * OrderPanel and everything under it read the process, unit and price straight
 * off the listing entity, so the buy flow is expressed by handing them a
 * listing that already says "purchase" rather than by threading a mode flag
 * through those components.
 *
 * Only publicData and price are swapped. The listing id, author, images and
 * stock stay as they are, because they are the same item either way.
 *
 * Returns the listing untouched for the rent mode, for non-dual listings, or
 * when the purchase price is missing — never a listing that claims a purchase
 * path it cannot price.
 *
 * @param {Object} listing the listing entity
 * @param {'rent'|'buy'|null} mode the chosen path
 * @param {string} currency marketplace currency
 * @returns {Object} the listing as the chosen mode sees it
 */
export const projectListingForMode = (listing, mode, currency) => {
  const publicData = listing?.attributes?.publicData;
  if (mode !== 'buy' || !canBuy(publicData)) {
    return listing;
  }

  const wholeUnits = publicData?.purchase_price;
  if (typeof wholeUnits !== 'number' || wholeUnits <= 0) {
    return listing;
  }

  let price;
  try {
    price = new sdkTypes.Money(Math.round(wholeUnits * unitDivisor(currency)), currency);
  } catch (e) {
    // Unsupported currency: leave the listing on its rental price rather than
    // show a purchase form priced at nothing.
    return listing;
  }

  return {
    ...listing,
    attributes: {
      ...listing.attributes,
      price,
      publicData: {
        ...publicData,
        transactionProcessAlias: publicData.sellProcessAlias,
        unitType: publicData.sellUnitType || 'item',
      },
    },
  };
};

export { isDualListingType };
